import { ExternalLink, FileBarChart } from "lucide-react";
import { Link, NavLink, Outlet, useOutletContext, useParams } from "react-router";
import { PRODUCT_LABEL, productCode } from "../../lib/format";
import { useMe, useProject } from "../../lib/queries";
import type { ProjectDetail, Role } from "../../lib/types";
import { Badge, StatusBadge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { ErrorState, PageSkeleton } from "../../ui/Feedback";
import styles from "./ProjectLayout.module.css";

/**
 * Tabs and the roles that use them (Buku Panduan Bab 13): admins see
 * everything. Client accounts (catalog v4) edit their own knowledge and
 * shape their bot's persona and widget, test it in the playground (paid
 * from saldo), and read conversations and saldo. Knowledge and Linkage are
 * curated by Navibiz staff.
 */
const TABS: { to: string; label: string; roles: Role[]; linkage?: boolean }[] = [
  { to: "", label: "Ringkasan", roles: ["specialist", "finance", "client"] },
  { to: "knowledge", label: "Pengetahuan", roles: ["specialist"] },
  { to: "links", label: "Linkage", roles: ["specialist"], linkage: true },
  { to: "playground", label: "Playground", roles: ["specialist", "client"] },
  { to: "tests", label: "Uji terima", roles: ["specialist"] },
  { to: "conversations", label: "Percakapan", roles: ["specialist", "client"] },
  { to: "saldo", label: "Saldo", roles: ["finance", "client"] },
  { to: "settings", label: "Pengaturan", roles: ["specialist", "client"] },
];

export function useProjectContext(): ProjectDetail {
  return useOutletContext<ProjectDetail>();
}

export function ProjectLayout() {
  const { projectId = "" } = useParams();
  const detail = useProject(projectId);
  const me = useMe().data!;

  if (detail.isPending) return <PageSkeleton />;
  if (detail.isError) return <ErrorState error={detail.error} />;
  const { project, knowledge } = detail.data;
  // Staff see Linkage for every project, to prepare it before an upgrade.
  const tabs = TABS.filter((tab) => me.role === "admin" || tab.roles.includes(me.role));

  return (
    <div className={styles.layout}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <div className="kicker">{project.clientName}</div>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{project.name}</h1>
            {project.archivedAt ? <Badge tone="neutral">Diarsipkan</Badge> : <StatusBadge status={project.status} />}
            <Badge tone="accent">{project.packageCode ? `${project.packageCode} + ${productCode(project.product)}` : PRODUCT_LABEL[project.product]}</Badge>
          </div>
          <p className={styles.meta}>
            {project.websiteUrl && (
              <a href={project.websiteUrl} target="_blank" rel="noreferrer">
                {project.websiteUrl.replace(/^https?:\/\//, "")} <ExternalLink aria-hidden />
              </a>
            )}
            <span>Model {project.model}</span>
            <span>{project.publishedVersion ? `Pengetahuan v${project.publishedVersion}` : "Belum ada pengetahuan terbit"}</span>
            {knowledge.unpublishedChanges && <span className={styles.draftNote}>Ada perubahan belum terbit</span>}
          </p>
        </div>
        <Link to="report">
          <Button icon={<FileBarChart aria-hidden />}>Laporan bulanan</Button>
        </Link>
      </header>

      <nav className={styles.tabs} aria-label="Bagian proyek">
        {tabs.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.to === ""} className={({ isActive }) => `${styles.tab} ${isActive ? styles.active : ""}`}>
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet context={detail.data} />
    </div>
  );
}
